import {
    computed, Ref, toRefs, unref,
} from 'vue';
import { PartialPaymentTourProduct } from '@ve/components/partialPayment/common/types/PartialPaymentTourProduct';
import { PartialPaymentProductType } from '@ve/components/partialPayment/common/types/PartialPaymentProductType';
import getMinFirstPaymentPartFromProducts from '@/js/common/Domain/Service/getMinFirstPaymentPartFromProducts';
import useProductsByType from '@ve/components/partialPayment/common/use/useProductsByType';

interface IProps {
    countryName: string;
    products: PartialPaymentTourProduct[];
    productType: PartialPaymentProductType;
    hotelPageProduct: boolean;
}

interface IReturn {
    titleText: Ref<string>;
    subTitleText: Ref<string>;
    isVisibleBanner: Ref<boolean>;
}

const usePartialPaymentBannerForHotelPage = (props:IProps):IReturn => {
    const { products, productType, hotelPageProduct } = toRefs(props);

    const { onlyTourOfferProducts, onlyHotelOfferProducts } = useProductsByType({ products, hotelPageProduct, productType });

    const minPercentForTours:Ref<number> = computed(() => (getMinFirstPaymentPartFromProducts(unref(onlyTourOfferProducts))));

    const minPercentForHotels:Ref<number> = computed(() => (getMinFirstPaymentPartFromProducts(unref(onlyHotelOfferProducts))));

    const minRoundedPercentForTours = computed(() => {
        const minPercentForToursValue = unref(minPercentForTours);
        if (minPercentForToursValue && minPercentForToursValue !== Infinity) return Math.round(minPercentForToursValue * 100);
        return 0;
    });

    const minRoundedPercentForHotels = computed(() => {
        const minPercentForHotelsValue = unref(minPercentForHotels);
        if (minPercentForHotelsValue && minPercentForHotelsValue !== Infinity) return Math.round(minPercentForHotelsValue * 100);
        return 0;
    });

    const hasTourOffersWithPartialPayment = computed(() => (unref(minRoundedPercentForTours) !== 0));

    const hasHotelOffersWithPartialPayment = computed(() => (unref(minRoundedPercentForHotels) !== 0));

    const titleText = computed(() => {
        const productTypeValue = unref(productType);
        const tourPercent = unref(minRoundedPercentForTours);
        const hotelPercent = unref(minRoundedPercentForHotels);

        let text = 'Оплатите';
        if (productTypeValue === 'tourOffer') text += ` ${tourPercent}`;
        if (productTypeValue === 'hotelOffer') text += ` ${hotelPercent}`;
        if (productTypeValue === 'all') {
            if (tourPercent !== hotelPercent) {
                if (unref(hasTourOffersWithPartialPayment) && unref(hasHotelOffersWithPartialPayment)) {
                    text += ` ${tourPercent} или ${hotelPercent}`;
                } else {
                    if (tourPercent) text += ` ${tourPercent}`;
                    if (hotelPercent) text += ` ${hotelPercent}`;
                }
            } else {
                text += ` ${tourPercent}`;
            }
        }
        text += ' % сейчас, остальное потом!';
        return text;
    });

    const subTitleText = computed(() => {
        const productTypeValue = unref(productType);
        if (productTypeValue === 'tourOffer') {
            return `Бронируйте тур заранее — платите только ${unref(minRoundedPercentForTours)}% стоимости. Услуга предоставляется бесплатно.`;
        }
        if (productTypeValue === 'hotelOffer') {
            return `Бронируйте отель заранее — платите только ${unref(minRoundedPercentForHotels)}% стоимости. Услуга предоставляется бесплатно.`;
        }

        return 'Бронируйте заранее — платите только часть стоимости. \r\n Услуга предоставляется бесплатно.';
    });

    const isVisibleBanner = computed(() => {
        const productTypeValue = unref(productType);
        if (productTypeValue === 'tourOffer') return unref(hasTourOffersWithPartialPayment);
        if (productTypeValue === 'hotelOffer') return unref(hasHotelOffersWithPartialPayment);
        if (productTypeValue === 'railwayTour') return false;

        return unref(hasTourOffersWithPartialPayment) || unref(hasHotelOffersWithPartialPayment);
    });

    return {
        titleText,
        subTitleText,
        isVisibleBanner,
    };
};

export default usePartialPaymentBannerForHotelPage;
